import { Injectable, inject } from "@angular/core"
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EntityAdapter, EntityState, createEntityAdapter } from "@ngrx/entity"
import { mergeMap, Observable, of, switchMap, tap, withLatestFrom } from "rxjs"
import { BackEndService } from "src/app/services/backend.service"
import { NamedSearchParameters } from "src/app/shared/components/filtering-search-component-base/filtering-search-types"


export interface ItemWithID {
    id: number
    snippets: string | undefined
}

export interface ListingState {
    window: number,
    present: number,
    total: number
}

export interface DocumentsState<Document extends ItemWithID> extends EntityState<Document> {
    loadingAt: number
    loading: boolean
    error: boolean
    listing: ListingState,
    search: NamedSearchParameters,
}
const adapter: EntityAdapter<ItemWithID> = createEntityAdapter<ItemWithID>()
const {
    selectAll
} = adapter.getSelectors()

@Injectable()
export class DocumentsStore<Document extends ItemWithID> extends ComponentStore<DocumentsState<Document>> {
    protected backendService = inject(BackEndService)
    protected adapter = adapter

    constructor() {
        super({
            ids: [],
            entities: {},
            loading: true,
            loadingAt: 0,
            error: false,
            search: {
                parameters: {},
                order: {
                    column: '',
                    direction: ''
                }

            },
            listing: {
                window: 50,
                present: 0,
                total: 0
            }
        })
    }

    readonly updateSearch = this.effect<NamedSearchParameters>(
        (search$) => search$.pipe(
            tap(search => this.patchState({
                ids: [], entities: {},
                search,
                listing: {
                    window: 50,
                    present: 0,
                    total: 0
                }
            })),
            tap(() => this.getDocuments())
        )
    )


    readonly getDocuments = this.effect<void>((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.select(state => state)),
            mergeMap(([_void, state]) => {
                if (state.loadingAt !== state.listing.present + 1) {
                    this.patchState({ loading: true, error: false, loadingAt: state.listing.present + 1})
                    return this.backendService.search({
                        ...state.search,
                        window: {
                            start: state.listing.present + 1,
                            size: state.listing.window,
                        },
                    }).pipe(
                        tapResponse(
                            (result: any) => {
                                state = this.adapter.upsertMany(result.articles, state)
                                return this.patchState({ ...state, loading: false, error: false, listing: result.listing })
                            },
                            (error) => this.patchState({ loading: false, error: true }),
                        )
                    )
                } else {
                    return of({})
                }
            }
            )
        )
    })


    readonly updateDocument = this.effect<Document>(
        (trigger$) => trigger$.pipe(
            tap((document) => {
                const state = this.get()
                this.patchState({
                    ...state,
                    entities: {
                        ...state.entities,
                        [document.id]: {
                            document
                        }
                    }
                })
            })
        )
    )

    readonly removeAll = this.effect<void>(
        (trigger$) => trigger$.pipe(
            tapResponse(
                () => this.patchState({
                    ids: [],
                    entities: {} ,
                    listing: {
                        window: 50,
                        present: 0,
                        total: 0
                    }
                }),
                (error) => error
            )
        )
    )

    readonly documents$: Observable<Document[]> = this.select(state => selectAll(state).sort((a, b) => a.id - b.id)) as Observable<Document[]>
    readonly loading$: Observable<boolean> = this.select(state => state.loading)
    readonly error$: Observable<boolean> = this.select(state => state.error)
    readonly listing$: Observable<ListingState> = this.select(state => state.listing)
    readonly search$: Observable<NamedSearchParameters> = this.select(state => state.search)
}